(() => {
  const onReady = () => {
    const body = document.body;
    const drawer = document.querySelector('[data-tc-drawer]');
    const screen = document.querySelector('.tc-screen');
    const search = document.querySelector('[data-tc-search]');
    const menuTriggers = [...document.querySelectorAll('[data-tc-menu-open]')];
    const searchTriggers = [...document.querySelectorAll('[data-tc-search-open], [data-ms-search-open]')];
    const cartTriggers = [...document.querySelectorAll('[aria-controls="cart-drawer"]')];

    if (!drawer && !search) return;

    let restoreFocusTo = null;
    let previousBodyOverflow = '';
    let scrollLocked = false;
    let savedScrollY = 0;
    let savedBodyStyle = {};

    const isDrawerOpen = () => Boolean(drawer?.classList.contains('is-open'));
    const isSearchOpen = () => Boolean(search?.classList.contains('is-open'));

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'summary',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const focusFirst = (container) => {
      if (!container) return;
      const preferred = container.matches('[data-tc-search]')
        ? container.querySelector('input[type="search"], [data-tc-search-close]')
        : container.querySelector('[data-tc-close], input[type="search"]');
      const first = preferred || container.querySelector(focusableSelector);
      if (first instanceof HTMLElement) first.focus({ preventScroll: true });
    };

    const lockScroll = () => {
      if (scrollLocked) return;
      scrollLocked = true;
      previousBodyOverflow = body.style.overflow;
      savedScrollY = window.scrollY;
      savedBodyStyle = { position: body.style.position, top: body.style.top, width: body.style.width };
      body.classList.add('db-ui-locked');
      document.documentElement.classList.add('db-ui-locked');
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.top = '-' + savedScrollY + 'px';
      body.style.width = '100%';
    };

    const unlockScroll = () => {
      if (isDrawerOpen() || isSearchOpen()) return;
      if (!scrollLocked) return;
      body.classList.remove('db-ui-locked');
      body.style.overflow = previousBodyOverflow;
      Object.assign(body.style, savedBodyStyle);
      window.scrollTo(0, savedScrollY);
      document.documentElement.classList.remove('db-ui-locked');
      previousBodyOverflow = '';
      scrollLocked = false;
    };

    const syncA11y = () => {
      const drawerOpen = isDrawerOpen();
      const searchOpen = isSearchOpen();
      const overlayOpen = drawerOpen || searchOpen;

      menuTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', drawerOpen ? 'true' : 'false');
      });
      searchTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-expanded', searchOpen ? 'true' : 'false');
      });
      drawer?.setAttribute('aria-hidden', drawerOpen ? 'false' : 'true');
      search?.setAttribute('aria-hidden', searchOpen ? 'false' : 'true');
      screen?.setAttribute('aria-hidden', overlayOpen ? 'false' : 'true');
      if (drawer) drawer.toggleAttribute('inert', !drawerOpen);
      if (search) search.toggleAttribute('inert', !searchOpen);
      if (screen) screen.toggleAttribute('inert', !overlayOpen);
      document.querySelectorAll('.tc-header-shell, #MainContent, .ac-theme-footer-shell, .apex-mobile-nav').forEach((element) => { element.toggleAttribute('inert', overlayOpen); });
    };

    const syncCartA11y = () => {
      const open = Boolean(document.querySelector('#cart-drawer[open], #cart-drawer dialog[open]'));
      cartTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', open ? 'true' : 'false'));
    };

    const returnFocus = () => {
      const target = restoreFocusTo;
      restoreFocusTo = null;
      if (target instanceof HTMLElement && document.contains(target)) {
        target.focus({ preventScroll: true });
      }
    };

    const closeDrawer = (restore = true) => {
      drawer?.classList.remove('is-open');
      syncA11y();
      if (restore && !isSearchOpen()) returnFocus();
      unlockScroll();
    };

    const closeSearch = (restore = true) => {
      search?.classList.remove('is-open');
      syncA11y();
      if (restore && !isDrawerOpen()) returnFocus();
      unlockScroll();
    };

    const openDrawer = (event) => {
      event?.preventDefault();
      if (!drawer) return;
      restoreFocusTo = event?.currentTarget instanceof HTMLElement ? event.currentTarget : document.activeElement;
      if (isSearchOpen()) closeSearch(false);
      drawer.classList.add('is-open');
      lockScroll();
      syncA11y();
      window.setTimeout(() => focusFirst(drawer), 50);
    };

    const openSearch = (event) => {
      event?.preventDefault();
      if (!search) return;
      restoreFocusTo = event?.currentTarget instanceof HTMLElement ? event.currentTarget : document.activeElement;
      if (isDrawerOpen()) closeDrawer(false);
      search.classList.add('is-open');
      lockScroll();
      syncA11y();
      window.setTimeout(() => focusFirst(search), 50);
    };

    menuTriggers.forEach((trigger) => trigger.addEventListener('click', openDrawer));
    searchTriggers.forEach((trigger) => trigger.addEventListener('click', openSearch));
    document.querySelectorAll('[data-tc-close]').forEach((element) => {
      element.addEventListener('click', () => closeDrawer());
    });
    document.querySelectorAll('[data-tc-search-close]').forEach((element) => {
      element.addEventListener('click', () => closeSearch());
    });

    search?.addEventListener('click', (event) => {
      if (event.target === search) closeSearch();
    });

    document.addEventListener('theme-drawer:open', syncCartA11y);
    document.addEventListener('theme-drawer:close', syncCartA11y);

    document.addEventListener('keydown', (event) => {
      const activeLayer = isSearchOpen() ? search : isDrawerOpen() ? drawer : null;
      if (!activeLayer) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        if (isSearchOpen()) closeSearch();
        else closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = [...activeLayer.querySelectorAll(focusableSelector)].filter(
        (element) => element instanceof HTMLElement && element.getClientRects().length > 0
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    syncA11y();
    syncCartA11y();
    const cart = document.querySelector('#cart-drawer');
    if (cart) new MutationObserver(syncCartA11y).observe(cart, { attributes: true, subtree: true, attributeFilter: ['open'] });
    document.querySelectorAll('.tech-usecases').forEach((section) => {
      const track = section.querySelector('.tech-usecases__track');
      const previous = section.querySelector('[data-prev]');
      const next = section.querySelector('[data-next]');
      if (!track || !previous || !next) return;
      const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
      const sync = () => {
        const position = Math.abs(track.scrollLeft);
        previous.disabled = position <= 2;
        next.disabled = position >= track.scrollWidth - track.clientWidth - 2;
      };
      const move = (step) => {
        const card = track.querySelector('.tech-usecase');
        const distance = (card?.getBoundingClientRect().width || track.clientWidth) + (parseFloat(getComputedStyle(track).columnGap) || 12);
        track.scrollBy({ left: step * direction * distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };
      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('scroll', sync, { passive: true });
      new ResizeObserver(sync).observe(track);
      sync();
    });
    const referenceInput = document.querySelector('[data-db-contact-reference]');
    if (referenceInput instanceof HTMLInputElement) {
      const params = new URLSearchParams(window.location.search);
      const productName = (params.get('producto') || '').slice(0,220);
      const reference = (params.get('referencia') || '').slice(0,100);
      if (!referenceInput.value) referenceInput.value = [productName, reference].filter(Boolean).join(' · ');
      const topic = document.getElementById('DbTopic');
      const requestedTopic = params.get('motivo');
      if (topic instanceof HTMLSelectElement && [...topic.options].some((option) => option.value === requestedTopic)) topic.value = requestedTopic;
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady, { once: true });
  else onReady();
})();
